// Public routes of otto-app: the check-in page, the thank-you page, health.
import { z } from "zod";
import { ConsentNotice, DuckSettings, MicSettings } from "@otto/config";
import { AgendaItemView, EndReason as LiveEndReason, Mode } from "@otto/protocol";
import { ConsultIdParams, IsoTime, Key, Ok, route, typedText } from "./common";

/**
 * Check-in industry: a list plus "Other" with free text (Wes, Sept 28 2026).
 * The first eight are today's check-in choices (ottos-consult src/ConsultPage.jsx).
 */
export const INDUSTRY_CHOICES = [
  "Small Business",
  "Medical",
  "Legal",
  "Home Services",
  "Nonprofit",
  "Professional Services",
  "Real Estate",
  "Construction",
  "Other",
] as const;
export const Industry = z.enum(INDUSTRY_CHOICES);

export const Language = z.enum(["en", "es"]);

/**
 * The browser's share of the pinned VoiceSettings plus the two switches the
 * browser acts on (DUCK_ENABLED; MIC_GATE_MODE, already applied to
 * mic.gate_mode). Pinned at check-in like the bundle.
 */
export const ClientAudioSettings = z.object({
  mic: MicSettings,
  duck: DuckSettings,
  /** DUCK_ENABLED as the browser should apply it (ships OFF). */
  duck_enabled: z.boolean(),
  /** Delay after the lip-sync analyser (design 7.3). null: the web app's own default. */
  lipsync_delay_ms: z.number().int().min(0).max(200).nullable(),
});
export type ClientAudioSettings = z.infer<typeof ClientAudioSettings>;

/** Everything the check-in page needs before the owner types anything. No personal data. */
export const CheckinConfigResponse = z.object({
  ok: Ok,
  /** false while CONSULTS_ENABLED is off, DRAIN is on, or a cap is reached. */
  open: z.boolean(),
  closed_reason: z.enum(["paused", "between_calls", "booked_up", "legacy"]).nullable(),
  legacy_url: z.url().nullable(),
  /** Public by design (Turnstile site keys are not secrets). */
  turnstile_site_key: z.string(),
  modes: z.object({ video: z.boolean(), voice: z.boolean() }),
  languages: z.array(Language).min(1),
  industries: z.array(Industry),
  /** The consent notice the page must show; its version is echoed back in the check-in. Mirrors consent_notice.version. */
  consent_notice_version: z.string().min(1).max(64).nullable(),
  /**
   * The notice itself (design 9.2). null: none is configured, and the
   * check-in is closed (it never runs without one).
   */
  consent_notice: ConsentNotice.nullable(),
  /** The privacy page beside the consent box. null: the web app's own default. */
  privacy_url: z.url().nullable(),
  /** "Prefer not to talk?" fallback and the thank-you callback link. null: the web app's own default. */
  callback_url: z.url().nullable(),
  agenda: z.array(AgendaItemView),
  target_minutes: z.number().positive(),
  cap_minutes: z.number().positive(),
  /** null only when no config bundle is active (then the check-in is closed anyway). */
  client_audio: ClientAudioSettings.nullable(),
});
export type CheckinConfigResponse = z.infer<typeof CheckinConfigResponse>;

const UtmKey = z.enum(["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]);

/**
 * Test consults (design 8.1): only a script holding TEST_CHECKIN_SECRET may
 * mark one, with these two headers on POST /api/checkin. The kind is a
 * TestKind from @otto/package-schema (canary, golden_set, full_audio, ...).
 */
export const TEST_CONSULT_HEADER = "x-otto-test" as const;
export const TEST_CONSULT_KEY_HEADER = "x-otto-test-key" as const;

export const CheckinRequest = z
  .strictObject({
    turnstile_token: z.string().min(1).max(4096),
    full_name: typedText(200),
    business_name: typedText(200),
    email: z.email().max(320),
    /** As typed; otto-app normalizes to E.164 and refuses what it cannot (phone is required). */
    phone: z.string().min(7).max(40),
    industry: Industry,
    /** Required when industry is "Other". */
    industry_other: typedText(200).optional(),
    mode: Mode,
    language: Language,
    consent: z.strictObject({
      /** The required box next to Start (design 9.2). */
      agreed: z.literal(true),
      notice_version: z.string().min(1).max(64),
    }),
    /** ?ch= on the link. Unknown or inactive channels fall back to general. */
    channel: Key.optional(),
    /** ?src= on the link: the member organization or campaign code. */
    source_code: z.string().regex(/^[a-z0-9_-]{1,64}$/i).optional(),
    utm: z.partialRecord(UtmKey, z.string().max(200)).optional(),
    referrer: z.string().max(2000).optional(),
    landing_url: z.string().max(2000).optional(),
  })
  .superRefine((req, ctx) => {
    if (req.industry === "Other" && !req.industry_other) {
      ctx.addIssue({ code: "custom", path: ["industry_other"], message: "Tell us your industry" });
    }
  });
export type CheckinRequest = z.infer<typeof CheckinRequest>;

/**
 * Success also sets two cookies (design 3.4 step 2):
 *   otto_consult  HttpOnly, the consult token (only its hash is stored); reaches only this consult
 *   the live token is ALSO returned in the body because the browser sends it in `hello`
 */
export const CheckinResponse = z.object({
  ok: Ok,
  consult_id: z.uuid(),
  /** "/live/<consult_id>" on the live host. */
  live_path: z.string().startsWith("/live/"),
  /**
   * Where otto-live answers. null: the page's own origin (a Workers route
   * sends /live/* to otto-live, or the dev server proxies it). Set when
   * otto-live has its own host (otto-app's LIVE_PUBLIC_ORIGIN).
   */
  live_origin: z.url().nullable(),
  live_token: z.string().min(1),
  live_token_expires_at: IsoTime,
  mode: Mode,
  language: Language,
  agenda: z.array(AgendaItemView),
  target_minutes: z.number().positive(),
  cap_minutes: z.number().positive(),
  client_audio: ClientAudioSettings,
});
export type CheckinResponse = z.infer<typeof CheckinResponse>;

/**
 * Finishing an unfinished consult from the emailed link (CR-015, Oct 18 2026).
 * The token is the link's fragment, "<consult_id>.<random>"; it travels only
 * in a POST body, never in a URL a server logs.
 */
export const ResumeToken = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.[A-Za-z0-9_-]{20,100}$/);
export const ResumeInfoRequest = z.strictObject({ token: ResumeToken });
export const ResumeInfoResponse = z.object({
  ok: Ok,
  first_name: z.string().max(100),
  business_name: z.string().max(200),
  language: Language,
  mode: Mode,
});
export type ResumeInfoResponse = z.infer<typeof ResumeInfoResponse>;
export const ResumeRequest = z.strictObject({
  token: ResumeToken,
  mode: Mode,
  consent: z.strictObject({
    agreed: z.literal(true),
    notice_version: z.string().min(1).max(64),
  }),
});
export type ResumeRequest = z.infer<typeof ResumeRequest>;
/** The same continuation from the same browser (CR-016): the consult cookie proves it, so no token. */
export const ResumeHereRequest = ResumeRequest.omit({ token: true });
export type ResumeHereRequest = z.infer<typeof ResumeHereRequest>;
export const ResumableResponse = z.discriminatedUnion("resumable", [
  z.object({ ok: Ok, resumable: z.literal(false) }),
  ResumeInfoResponse.extend({ resumable: z.literal(true) }),
]);
export type ResumableResponse = z.infer<typeof ResumableResponse>;
/** Why a link cannot be used (ApiErrorBody.error.code, status 404 or 410). */
export const RESUME_ERROR_CODES = ["resume_invalid", "resume_used", "resume_expired"] as const;

/** Error codes the check-in can answer with (ApiErrorBody.error.code). */
export const CHECKIN_ERROR_CODES = [
  "invalid_input",
  "turnstile_failed",
  "rate_limited",
  "paused", // CONSULTS_ENABLED off
  "between_calls", // DRAIN on
  "legacy", // ROUTE_TO_LEGACY on; redirect_url set
  "booked_up", // daily ceiling or daily spend cap
  "at_capacity", // concurrency cap
  "mode_unavailable",
  "language_unavailable",
  "internal",
] as const;

/** Consult lifecycle, stored in consults.status. */
export const ConsultStatus = z.enum([
  "checked_in", // row written, call not started
  "live", // a live socket is open
  "ended", // call over, package not started
  "packaging", // package job running
  "packaged", // package stored (and handed off if the switch is on)
  "held", // the substance gate held it as unfinished
  "failed", // package job failed after retries (dead letter, alerted)
  "withdrawn", // the owner withdrew consent; transcript deleted
  "abandoned", // checked in, never started
]);
export type ConsultStatus = z.infer<typeof ConsultStatus>;

/**
 * The thank-you page's plain states (design 8.7; Wes's list, Sept 28 2026):
 *   saving     the call is over and being saved
 *   preparing  the package is being built
 *   ready      the package is stored (and handed over if the webhook is on)
 *   held       the substance gate held it: "a few more minutes with you"
 *   delayed    still not ready 10 minutes after the end
 * null for withdrawn and abandoned consults.
 */
export const ThankYouPhase = z.enum(["saving", "preparing", "ready", "held", "delayed"]);
export type ThankYouPhase = z.infer<typeof ThankYouPhase>;

export const StatusResponse = z.object({
  ok: Ok,
  consult_id: z.uuid(),
  status: ConsultStatus,
  end_reason: LiveEndReason.nullable(),
  /** The approved, guarded read-back ("What Otto heard"); null until it exists. */
  what_otto_heard: z.string().max(1000).nullable(),
  human_requested: z.boolean(),
  can_resume: z.boolean(),
  phase: ThankYouPhase.nullable(),
  /**
   * true once the package webhook accepted the package (design 8.7 "being
   * prepared"). It is NOT "the report was emailed": no report.sent receiver
   * exists in this phase, so the page never claims an email was sent.
   */
  handed_over: z.boolean(),
});
export type StatusResponse = z.infer<typeof StatusResponse>;

export const RatingRequest = z.strictObject({
  score: z.number().int().min(1).max(5),
  comment: typedText(1000).optional(),
});

/**
 * A fresh live token for this consult, for a new tab or the thank-you page's
 * Resume (the token otherwise lives only in the tab that checked in). Needs
 * the HttpOnly consult cookie; refused once the consult has ended.
 */
export const LiveTokenResponse = z.object({
  ok: Ok,
  consult_id: z.uuid(),
  live_path: z.string().startsWith("/live/"),
  live_origin: z.url().nullable(),
  live_token: z.string().min(1),
  live_token_expires_at: IsoTime,
  mode: Mode,
  language: Language,
  first_name: z.string().max(100),
  agenda: z.array(AgendaItemView),
  target_minutes: z.number().positive(),
  cap_minutes: z.number().positive(),
  client_audio: ClientAudioSettings,
});
export type LiveTokenResponse = z.infer<typeof LiveTokenResponse>;

/** Counts-only funnel steps (design 3.4 step 1): no name, email, phone, IP or cookie. */
export const FUNNEL_STEPS = [
  "page_view",
  "form_started",
  "mode_chosen",
  "mic_granted",
  "mic_denied",
  "start_tapped",
  "checked_in",
  "call_connected",
  "call_ended",
  "in_app_browser_seen",
  "person_request_opened",
] as const;
export const FunnelStep = z.enum(FUNNEL_STEPS);
export type FunnelStep = z.infer<typeof FunnelStep>;

export const FunnelEvent = z.strictObject({
  step: FunnelStep,
  device_class: z.enum(["phone", "tablet", "desktop", "unknown"]),
  in_app_browser: z.boolean(),
  mode: Mode.optional(),
  language: Language.optional(),
  channel: Key.optional(),
});
export type FunnelEvent = z.infer<typeof FunnelEvent>;

/**
 * "Prefer not to talk? A person will reach out" (design 7.7): the check-in
 * details plus how the owner wants to be reached. No consult, no call. Stored
 * and posted to the team (like Close event E9, which is not in this phase).
 * Turnstile guards it like the check-in.
 */
export const HumanRequest = z
  .strictObject({
    turnstile_token: z.string().min(1).max(4096),
    full_name: typedText(200),
    business_name: typedText(200),
    email: z.email().max(320),
    phone: z.string().min(7).max(40),
    industry: Industry,
    industry_other: typedText(200).optional(),
    language: Language,
    contact_method: z.enum(["email", "text", "call"]),
    channel: Key.optional(),
    source_code: z.string().regex(/^[a-z0-9_-]{1,64}$/i).optional(),
  })
  .superRefine((req, ctx) => {
    if (req.industry === "Other" && !req.industry_other) {
      ctx.addIssue({ code: "custom", path: ["industry_other"], message: "Tell us your industry" });
    }
  });
export type HumanRequest = z.infer<typeof HumanRequest>;

export const HealthResponse = z.object({
  ok: z.boolean(),
  service: z.enum(["otto-app", "otto-live"]),
  time: IsoTime,
  /** Commit or tag shipped, when the deploy script sets it. */
  version: z.string().nullable(),
  checks: z.record(z.string(), z.enum(["ok", "fail", "skipped"])),
});
export type HealthResponse = z.infer<typeof HealthResponse>;

export const publicRoutes = {
  checkinConfig: route({ method: "GET", path: "/api/checkin/config", auth: "public", response: CheckinConfigResponse }),
  checkin: route({ method: "POST", path: "/api/checkin", auth: "public", body: CheckinRequest, response: CheckinResponse }),
  resumeInfo: route({ method: "POST", path: "/api/resume/info", auth: "public", body: ResumeInfoRequest, response: ResumeInfoResponse }),
  resume: route({ method: "POST", path: "/api/resume", auth: "public", body: ResumeRequest, response: CheckinResponse }),
  consultResumable: route({ method: "GET", path: "/api/consult/resumable", auth: "public", response: ResumableResponse }),
  consultResumeHere: route({ method: "POST", path: "/api/consult/resume", auth: "public", body: ResumeHereRequest, response: CheckinResponse }),
  consultStatus: route({
    method: "GET",
    path: "/api/consult/:id/status",
    auth: "consult_cookie",
    params: ConsultIdParams,
    response: StatusResponse,
  }),
  consultRating: route({
    method: "POST",
    path: "/api/consult/:id/rating",
    auth: "consult_cookie",
    params: ConsultIdParams,
    body: RatingRequest,
    response: z.object({ ok: Ok }),
  }),
  consultLiveToken: route({
    method: "GET",
    path: "/api/consult/:id/live-token",
    auth: "consult_cookie",
    params: ConsultIdParams,
    response: LiveTokenResponse,
  }),
  funnel: route({ method: "POST", path: "/api/funnel", auth: "public", body: FunnelEvent, response: z.object({ ok: Ok }) }),
  humanRequest: route({ method: "POST", path: "/api/human-request", auth: "public", body: HumanRequest, response: z.object({ ok: Ok }) }),
  health: route({ method: "GET", path: "/health", auth: "public", response: HealthResponse }),
} as const;
